import React from 'react';
import { getIconPath } from './iconMap';
import {
  Users, BarChart3, Wallet, Target, Search, Phone, FileText, Briefcase,
  Home, Settings, Palette, CheckCircle, X, RefreshCw, List, Lightbulb,
  Link2, ArrowRight, Hourglass, Smartphone, Save, Heart, Star, Building2,
  Calendar, MessageCircle,
} from 'lucide-react';

/** Стильные Lucide-иконки по эмодзи (приоритет над SVG) */
const lucideByEmoji = {
  '👥': Users, '📊': BarChart3, '💸': Wallet, '🎯': Target, '🔍': Search,
  '📞': Phone, '📝': FileText, '💼': Briefcase, '🏠': Home, '⚙️': Settings,
  '🎨': Palette, '✅': CheckCircle, '❌': X, '🔄': RefreshCw, '📋': List,
  '💡': Lightbulb, '🔗': Link2, '➡️': ArrowRight, '⏳': Hourglass,
  '📱': Smartphone, '💾': Save, '❤️': Heart, '⭐': Star, '🏛️': Building2,
  '📅': Calendar, '💬': MessageCircle,
};

// Статический импорт всех используемых иконок как URL
import PeopleIcon from './icons/social/people.svg';
import PersonIcon from './icons/social/person.svg';
import BarChartIcon from './icons/editor/bar_chart.svg';
import WalletIcon from './icons/Action/account_balance_wallet.svg';
import GpsFixedIcon from './icons/device/gps_fixed.svg';
import SearchIcon from './icons/Action/search.svg';
import WorkIcon from './icons/Action/work.svg';
import PhoneIcon from './icons/comminication/phone.svg';
import NotesIcon from './icons/editor/notes.svg';
import HomeIcon from './icons/navigation/home_work.svg';
import SwapVertIcon from './icons/Action/swap_vert.svg';
import TrendingUpIcon from './icons/Action/trending_up.svg';
import TrendingDownIcon from './icons/Action/trending_down.svg';
import SettingsIcon from './icons/Action/settings.svg';
import FormatPaintIcon from './icons/editor/format_paint.svg';
import CheckCircleIcon from './icons/Action/check_circle.svg';
import CancelIcon from './icons/navigation/cancel.svg';
import CachedIcon from './icons/Action/cached.svg';
import ListIcon from './icons/Action/list.svg';
import LightbulbIcon from './icons/Action/lightbulb_outline.svg';
import LinkIcon from './icons/content/link.svg';
import ArrowForwardIcon from './icons/navigation/arrow_forward.svg';
import HourglassIcon from './icons/Action/hourglass_empty.svg';
import DevicesIcon from './icons/device/devices.svg';
import SaveIcon from './icons/content/save.svg';
import FavoriteIcon from './icons/Action/favorite.svg';
import StarRateIcon from './icons/Action/star_rate.svg';
import AccountBalanceIcon from './icons/Action/account_balance.svg';

/**
 * Маппинг путей иконок на импортированные URL
 */
const iconMap = {
  'social/people': PeopleIcon,
  'social/person': PersonIcon,
  'editor/bar_chart': BarChartIcon,
  'Action/account_balance_wallet': WalletIcon,
  'device/gps_fixed': GpsFixedIcon,
  'Action/search': SearchIcon,
  'Action/work': WorkIcon,
  'comminication/phone': PhoneIcon,
  'editor/notes': NotesIcon,
  'navigation/home_work': HomeIcon,
  'Action/swap_vert': SwapVertIcon,
  'Action/trending_up': TrendingUpIcon,
  'Action/trending_down': TrendingDownIcon,
  'Action/settings': SettingsIcon,
  'editor/format_paint': FormatPaintIcon,
  'Action/check_circle': CheckCircleIcon,
  'navigation/cancel': CancelIcon,
  'Action/cached': CachedIcon,
  'Action/list': ListIcon,
  'Action/lightbulb_outline': LightbulbIcon,
  'content/link': LinkIcon,
  'navigation/arrow_forward': ArrowForwardIcon,
  'Action/hourglass_empty': HourglassIcon,
  'device/devices': DevicesIcon,
  'content/save': SaveIcon,
  'Action/favorite': FavoriteIcon,
  'Action/star_rate': StarRateIcon,
  'Action/account_balance': AccountBalanceIcon,
};

/**
 * Компонент для отображения SVG иконок
 * @param {string} name - Имя иконки (путь относительно icons/) или эмодзи
 * @param {string} size - Размер иконки (по умолчанию 24)
 * @param {string} color - Цвет иконки (по умолчанию currentColor)
 * @param {string} className - Дополнительные CSS классы
 * @param {string} emoji - Эмодзи для маппинга на иконку
 */
const Icon = ({ name, size = 24, color = 'currentColor', className = '', emoji = null }) => {
  // Приоритет: Lucide по эмодзи (стильные иконки)
  if (emoji && lucideByEmoji[emoji]) {
    const LucideIcon = lucideByEmoji[emoji];
    return <LucideIcon size={size} className={className} strokeWidth={2} />;
  }

  const iconName = emoji ? getIconPath(emoji) : name;
  if (!iconName) {
    if (emoji) {
      return <span className={className} style={{ fontSize: size }}>{emoji}</span>;
    }
    return null;
  }

  // Получаем URL иконки
  const iconUrl = iconMap[iconName];
  
  if (!iconUrl) {
    console.warn(`Icon not found: ${iconName}`);
    // Fallback на эмодзи, если передан
    if (emoji) {
      return <span className={className} style={{ fontSize: size }}>{emoji}</span>;
    }
    return null;
  }

  return (
    <img 
      src={iconUrl}
      alt={iconName}
      width={size}
      height={size}
      className={className}
      style={{ 
        display: 'inline-block',
        verticalAlign: 'middle',
        filter: color !== 'currentColor' && color !== 'white' ? `brightness(0) saturate(100%) invert(${getColorInvert(color)})` : 
                color === 'white' ? 'brightness(0) invert(1)' : undefined
      }}
    />
  );
};

/**
 * Получить значение инверсии для фильтра цвета
 */
const getColorInvert = (color) => {
  // Простая реализация для основных цветов
  if (color === 'white' || color === '#fff' || color === '#ffffff') return '1';
  return '0';
};

export default Icon;
